/**
 * Jurisdiction-Aware Footer Component
 * Always reflects correct legal jurisdiction based on user's language/country
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/lib/context';
import { useLegalContent, LANGUAGE_JURISDICTION_MAP } from '@/hooks/useLegalContent';

// Jurisdiction display names
const JURISDICTION_NAMES = {
  ee: 'Estonia',
  no: 'Norway',
  fi: 'Finland',
  se: 'Sweden',
  ru: 'Russia',
  en: 'International'
};

export default function Footer() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const { companyName, jurisdiction } = useLegalContent();
  
  const currentYear = new Date().getFullYear();
  const jurisdictionName = JURISDICTION_NAMES[jurisdiction] || 'International';

  return (
    <footer className="bg-gray-900 text-gray-300">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {/* Company */}
          <div>
            <h3 className="text-white font-semibold mb-4">KEIKKA</h3>
            <p className="text-sm text-gray-400 mb-2">
              {t('footer.tagline')}
            </p>
            <p className="text-xs text-gray-500">
              {companyName}
            </p>
          </div>

          {/* Marketplace */}
          <div>
            <h4 className="text-white font-medium mb-4">{t('footer.marketplace')}</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/jobs" className="hover:text-white transition-colors">{t('nav.jobs')}</Link></li>
              <li><Link to="/services" className="hover:text-white transition-colors">{t('nav.services')}</Link></li>
              <li><Link to="/rentals" className="hover:text-white transition-colors">{t('nav.rentals')}</Link></li>
              <li><Link to="/auto" className="hover:text-white transition-colors">{t('nav.autoRepair')}</Link></li>
            </ul>
          </div>

          {/* Legal - Jurisdiction Aware */}
          <div>
            <h4 className="text-white font-medium mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/legal/terms" className="hover:text-white transition-colors">
                  {t('footer.terms')}
                </Link>
              </li>
              <li>
                <Link to="/legal/privacy" className="hover:text-white transition-colors">
                  {t('footer.privacy')}
                </Link>
              </li>
              <li>
                <Link to="/legal/cookies" className="hover:text-white transition-colors">
                  {t('footer.cookies')}
                </Link>
              </li>
            </ul>
            {/* Jurisdiction indicator */}
            <p className="text-xs text-gray-500 mt-3">
              {t('footer.jurisdiction')}: {jurisdictionName}
            </p>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-medium mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="mailto:support@keikka.com" className="hover:text-white transition-colors">
                  support@keikka.com
                </a>
              </li>
              <li>
                <a href="mailto:legal@keikka.com" className="hover:text-white transition-colors">
                  legal@keikka.com
                </a>
              </li>
              <li>
                <Link to="/invest" className="hover:text-white transition-colors">
                  {t('footer.investors')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-800 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-gray-500">
            © {currentYear} {companyName}. {t('footer.allRightsReserved')}
          </p>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
              {jurisdictionName}
            </span>
            <span>|</span>
            <span className="uppercase">{language}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
